import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { superLogin as apiLogin, superMe, SUPER_TOKEN_KEY } from '../api/super';

// Platform-operator session. Deliberately separate from AuthContext: an
// operator is not a user inside any firm, and the two sessions must be able to
// coexist in one browser without either one overwriting the other's token.

interface SuperAdmin {
  id: number;
  email: string;
  name: string;
}

interface SuperAuthContextType {
  admin: SuperAdmin | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const Ctx = createContext<SuperAuthContextType | null>(null);

export const SuperAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [admin, setAdmin] = useState<SuperAdmin | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!localStorage.getItem(SUPER_TOKEN_KEY)) {
      setLoading(false);
      return;
    }
    // Re-validate against the server rather than trusting a stored token: the
    // operator may have been deactivated since it was issued.
    superMe()
      .then((res) => setAdmin(res.data))
      .catch(() => {
        localStorage.removeItem(SUPER_TOKEN_KEY);
        setAdmin(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await apiLogin(email, password);
    localStorage.setItem(SUPER_TOKEN_KEY, res.data.token);
    setAdmin(res.data.superAdmin);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(SUPER_TOKEN_KEY);
    setAdmin(null);
  }, []);

  return <Ctx.Provider value={{ admin, loading, login, logout }}>{children}</Ctx.Provider>;
};

export const useSuperAuth = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useSuperAuth must be used within SuperAuthProvider');
  return ctx;
};
